import { HighlightDroppable } from "@/components/forms/components/HighlightDroppable.tsx";
import {
  useModals,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { CustomFieldOptionEditModal } from "@/pages/settings/custom-fields/widgets/CustomFieldOptionEditModal.tsx";
import { OptionsBulkAddModal } from "@/components/options/OptionsBulkAddModal.tsx";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { CustomFieldOptionPayload } from "@/types/custom-field.types.ts";
import { DragDropContext, Draggable, DropResult } from "@hello-pangea/dnd";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface CustomFieldOptionsProps {
  options: CustomFieldOptionPayload[];
  updateOptions: Dispatch<SetStateAction<CustomFieldOptionPayload[]>>;
  highlightWarning?: boolean;
}

export function CustomFieldOptions({
  options,
  updateOptions,
  highlightWarning = false,
}: CustomFieldOptionsProps) {
  const { openModal } = useModals();
  const { t } = useTranslation();

  const editOption = (index?: number, option?: CustomFieldOptionPayload) => {
    openModal(CustomFieldOptionEditModal, {
      option,
      onUpdate: (updatedOption: CustomFieldOptionPayload, isNew: boolean) => {
        if (isNew) {
          updateOptions((prev) => [...prev, updatedOption]);
        } else {
          updateOptions((prev) =>
            prev.map((option, optIndex) => (index === optIndex ? updatedOption : option))
          );
        }
      },
    });
  };

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const { source, destination } = result;

      if (source.index === destination.index) {
        return;
      }

      updateOptions((prev) => {
        const newOptions = Array.from(prev);
        const [reorderedItem] = newOptions.splice(source.index, 1);
        newOptions.splice(destination.index, 0, reorderedItem);
        return newOptions;
      });
    },
    [options]
  );

  return (
    <Stack className={"mb-2"} gap={"sm"}>
      <Title paddingTop paddingBottom header={t("common:options")} size={"xs"}></Title>
      <Stack gap={"smd"}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <HighlightDroppable droppableId="options" type="option">
            {(provided) => (
              <Stack gap={"smd"} {...provided.droppableProps} ref={provided.innerRef}>
                {options.map((option, index) => (
                  <CustomFieldOptionItem
                    index={index}
                    editOption={editOption}
                    options={options}
                    option={option}
                    updateOptions={updateOptions}
                    key={`option-${index}`}
                  />
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </HighlightDroppable>
        </DragDropContext>
        <Stack direction={"horizontal"} gap={"sm"} justify={"between"}>
          <Button
            variant={highlightWarning ? "danger" : "plain"}
            className={"text-center w-1/2"}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            onClick={() => {
              editOption();
            }}
          >
            {t("common:add_option")}
          </Button>
          <Button
            variant={"plain"}
            className={"text-center w-1/2"}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-rectangle-history-circle-plus" />}
            onClick={() => {
              openModal(OptionsBulkAddModal, {
                onAdd: (titles: string[]) => {
                  const newOptions = titles.map((title) => ({ title, description: "" }));
                  updateOptions((prev) => [...prev, ...newOptions]);
                  titles.forEach(() => {
                    analyticsService.trackEvent(analyticEvents.customFields.optionCreated);
                  });
                },
              });
            }}
          >
            {t("common:add_multiple")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface CustomFieldOptionItemProps extends CustomFieldOptionsProps {
  option: CustomFieldOptionPayload;
  editOption: (index: number, option?: CustomFieldOptionPayload) => void;
  index: number;
}

function CustomFieldOptionItem({
  index,
  editOption,
  option,
  updateOptions,
}: CustomFieldOptionItemProps) {
  const { t } = useTranslation();

  const deleteOption = useCallback((aimIndex: number) => {
    updateOptions((prev) => prev.filter((_, index) => aimIndex !== index));

    analyticsService.trackEvent(analyticEvents.customFields.optionDeleted);
  }, []);

  return (
    <Draggable draggableId={`option-${index}`} index={index}>
      {(provided, snapshot) => (
        <Stack
          className={
            "rounded-xs bg-dark/5 border border-dark/5 py-1 pl-2 pr-1 group cursor-pointer"
          }
          justify={"between"}
          gap={"md"}
          direction={"horizontal"}
          items={"center"}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Text className={"font-semibold"}>{option.title}</Text>
          <Stack
            className={
              snapshot.isDragging ? "opacity-100" : "opacity-0 group-hover:opacity-100 transition"
            }
            direction={"horizontal"}
          >
            <ButtonIcon
              icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
              variant={"plain"}
              onClick={() => deleteOption(index)}
              title={t("common:delete")}
            />
            <ButtonIcon
              icon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
              variant={"plain"}
              onClick={() => editOption(index, option)}
              title={t("common:edit")}
            />
            <ButtonIcon
              className={"touch-none pointer-events-none"}
              icon={<FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />}
              variant={"plain"}
              title={t("common:drag_to_reorder")}
            />
          </Stack>
        </Stack>
      )}
    </Draggable>
  );
}
